export type KavooshTheme = "light" | "dark";
export type ThemePreference = KavooshTheme | "system";
export type ThemeResolutionSource = "explicit" | "stored" | "tenant" | "system" | "fallback";

export type ThemeResolutionInput = {
  explicitPreference?: unknown;
  storedPreference?: unknown;
  tenantDefault?: unknown;
  systemPrefersDark?: boolean;
};

export type ThemeResolution = {
  preference: ThemePreference;
  resolved: KavooshTheme;
  source: ThemeResolutionSource;
};

export const THEME_STORAGE_KEY = "kavoosh.theme";
export const SHARED_THEME_CHANGE_EVENT = "kavoosh:theme-change";
// Compatibility alias retained for source that still imports the pre-consolidation name.
export const THEME_PREFERENCE_EVENT = SHARED_THEME_CHANGE_EVENT;

export function parseThemePreference(value: unknown): ThemePreference | null {
  return value === "light" || value === "dark" || value === "system" ? value : null;
}

export function resolveThemePreference(preference: ThemePreference, systemPrefersDark: boolean): KavooshTheme {
  if (preference === "system") return systemPrefersDark ? "dark" : "light";
  return preference;
}

export function resolveTheme(input: ThemeResolutionInput): ThemeResolution {
  const systemPrefersDark = input.systemPrefersDark ?? false;
  const candidates: Array<{ value: unknown; source: ThemeResolutionSource }> = [
    { value: input.explicitPreference, source: "explicit" },
    { value: input.storedPreference, source: "stored" },
    { value: input.tenantDefault, source: "tenant" },
  ];

  for (const candidate of candidates) {
    const preference = parseThemePreference(candidate.value);
    if (preference) {
      return { preference, resolved: resolveThemePreference(preference, systemPrefersDark), source: candidate.source };
    }
  }

  if (typeof input.systemPrefersDark === "boolean") {
    return { preference: "system", resolved: input.systemPrefersDark ? "dark" : "light", source: "system" };
  }

  return { preference: "light", resolved: "light", source: "fallback" };
}

export function readThemePreference(storage: Pick<Storage, "getItem">): ThemePreference {
  return parseThemePreference(storage.getItem(THEME_STORAGE_KEY)) ?? "system";
}

export function applyThemePreference(
  preference: ThemePreference,
  options: {
    root?: HTMLElement;
    storage?: Pick<Storage, "setItem">;
    systemPrefersDark?: boolean;
  } = {},
): KavooshTheme {
  const root = options.root ?? document.documentElement;
  const systemPrefersDark = options.systemPrefersDark
    ?? (typeof window !== "undefined" && typeof window.matchMedia === "function"
      ? window.matchMedia("(prefers-color-scheme: dark)").matches
      : false);
  const resolved = resolveThemePreference(preference, systemPrefersDark);
  root.dataset.kavooshTheme = resolved;
  root.style.colorScheme = resolved;
  options.storage?.setItem(THEME_STORAGE_KEY, preference);
  return resolved;
}

/** Read the single Kavoosh theme preference shared by public/auth and authenticated workspaces. */
export function readSharedThemePreference(): ThemePreference {
  try {
    return readThemePreference(window.localStorage);
  } catch {
    return "system";
  }
}

/** Apply current authority without writing storage. Safe for media/storage synchronization. */
export function syncSharedThemePreference(preference = readSharedThemePreference()): KavooshTheme {
  return applyThemePreference(preference);
}

/** Persist explicit user intent, apply it, and notify same-tab consumers. */
export function setSharedThemePreference(preference: ThemePreference): KavooshTheme {
  let resolved: KavooshTheme;
  try {
    resolved = applyThemePreference(preference, { storage: window.localStorage });
  } catch {
    resolved = applyThemePreference(preference);
  }
  if (typeof window !== "undefined") {
    window.dispatchEvent(new CustomEvent<ThemePreference>(SHARED_THEME_CHANGE_EVENT, { detail: preference }));
  }
  return resolved;
}

/** Subscribe to both same-tab and cross-tab preference changes. */
export function subscribeSharedTheme(listener: (preference: ThemePreference) => void): () => void {
  if (typeof window === "undefined") return () => undefined;
  const local = (event: Event) => {
    const preference = (event as CustomEvent<ThemePreference>).detail;
    listener(parseThemePreference(preference) ?? readSharedThemePreference());
  };
  const storage = (event: StorageEvent) => {
    if (event.key === THEME_STORAGE_KEY) listener(readSharedThemePreference());
  };
  window.addEventListener(SHARED_THEME_CHANGE_EVENT, local);
  window.addEventListener("storage", storage);
  return () => {
    window.removeEventListener(SHARED_THEME_CHANGE_EVENT, local);
    window.removeEventListener("storage", storage);
  };
}

// Backward-compatible aliases. SharedTheme is the canonical API going forward.
export const readBrowserThemePreference = readSharedThemePreference;
export const syncBrowserThemePreference = syncSharedThemePreference;
export const setBrowserThemePreference = setSharedThemePreference;
export const subscribeBrowserThemePreference = subscribeSharedTheme;
